// -- XLSForm-style calculate expression evaluator --
// Supports the subset of XPath used by `calculate` expressions generated for
// scoring forms: ${fieldname} substitution, if(cond, a, b), comparisons
// (=, !=, >=, <=, >, <), arithmetic (+, -, *, div), round(val, decimals),
// string/number literals, and parentheses. Not a full XPath implementation --
// deliberately scoped to what these forms actually use.

function tokenize(expr) {
    const tokens = [];
    let i = 0;
    while (i < expr.length) {
        const ch = expr[i];
        if (/\s/.test(ch)) { i++; continue; }

        // ${fieldname}
        if (ch === '$' && expr[i+1] === '{') {
            const end = expr.indexOf('}', i);
            if (end === -1) throw new Error('Unclosed ${ in expression');
            tokens.push({ type: 'FIELDREF', value: expr.slice(i+2, end) });
            i = end + 1;
            continue;
        }

        // string literal
        if (ch === "'") {
            const end = expr.indexOf("'", i+1);
            if (end === -1) throw new Error("Unclosed string literal");
            tokens.push({ type: 'STRING', value: expr.slice(i+1, end) });
            i = end + 1;
            continue;
        }

        // number
        if (/[0-9]/.test(ch)) {
            let j = i;
            while (j < expr.length && /[0-9.]/.test(expr[j])) j++;
            tokens.push({ type: 'NUMBER', value: parseFloat(expr.slice(i, j)) });
            i = j;
            continue;
        }

        // two-char operators
        const two = expr.slice(i, i+2);
        if (two === '!=' || two === '>=' || two === '<=') {
            tokens.push({ type: 'OP', value: two });
            i += 2;
            continue;
        }

        // single-char operators/punctuation
        if ('=+-*(),><'.includes(ch)) {
            tokens.push({ type: 'OP', value: ch });
            i++;
            continue;
        }

        // identifier (if, round, div, or other words)
        if (/[a-zA-Z_]/.test(ch)) {
            let j = i;
            while (j < expr.length && /[a-zA-Z0-9_]/.test(expr[j])) j++;
            tokens.push({ type: 'IDENT', value: expr.slice(i, j) });
            i = j;
            continue;
        }

        throw new Error(`Unexpected character '${ch}' at position ${i} in expression`);
    }
    return tokens;
}

function parse(tokens) {
    let pos = 0;
    const peek = () => tokens[pos];
    const next = () => tokens[pos++];
    const expect = (type, value) => {
        const t = next();
        if (!t || t.type !== type || (value !== undefined && t.value !== value)) {
            throw new Error(`Expected ${type} ${value||''}, got ${t ? t.type+' '+t.value : 'EOF'}`);
        }
        return t;
    };

    // comparison: additive ( (=|!=|>=|<=|>|<) additive )?
    function parseComparison() {
        let left = parseAdditive();
        const t = peek();
        if (t && t.type === 'OP' && ['=','!=','>=','<=','>','<'].includes(t.value)) {
            const op = next().value;
            const right = parseAdditive();
            return { kind: 'compare', op, left, right };
        }
        return left;
    }

    // additive: multiplicative ( (+|-) multiplicative )*
    function parseAdditive() {
        let left = parseMultiplicative();
        while (peek() && peek().type === 'OP' && (peek().value === '+' || peek().value === '-')) {
            const op = next().value;
            const right = parseMultiplicative();
            left = { kind: 'binop', op, left, right };
        }
        return left;
    }

    // multiplicative: primary ( (*|div) primary )*
    function parseMultiplicative() {
        let left = parsePrimary();
        while (peek() && ((peek().type === 'OP' && peek().value === '*') ||
                           (peek().type === 'IDENT' && peek().value === 'div'))) {
            const op = next().value;
            const right = parsePrimary();
            left = { kind: 'binop', op, left, right };
        }
        return left;
    }

    function parsePrimary() {
        const t = peek();
        if (!t) throw new Error('Unexpected end of expression');

        if (t.type === 'NUMBER') { next(); return { kind: 'num', value: t.value }; }
        if (t.type === 'STRING') { next(); return { kind: 'str', value: t.value }; }
        if (t.type === 'FIELDREF') { next(); return { kind: 'field', name: t.value }; }

        if (t.type === 'OP' && t.value === '(') {
            next();
            const inner = parseComparison();
            expect('OP', ')');
            return inner;
        }
        if (t.type === 'OP' && t.value === '-') {
            next();
            const inner = parsePrimary();
            return { kind: 'neg', inner };
        }

        if (t.type === 'IDENT') {
            const name = next().value;
            if (name === 'if') {
                expect('OP', '(');
                const cond = parseComparison();
                expect('OP', ',');
                const a = parseComparison();
                expect('OP', ',');
                const b = parseComparison();
                expect('OP', ')');
                return { kind: 'if', cond, a, b };
            }
            if (name === 'round') {
                expect('OP', '(');
                const val = parseComparison();
                expect('OP', ',');
                const decimals = parseComparison();
                expect('OP', ')');
                return { kind: 'round', val, decimals };
            }
            throw new Error(`Unknown function/identifier: ${name}`);
        }

        throw new Error(`Unexpected token: ${t.type} ${t.value}`);
    }

    const result = parseComparison();
    if (pos < tokens.length) throw new Error(`Unexpected trailing tokens starting at ${JSON.stringify(tokens[pos])}`);
    return result;
}

function evaluate(node, getValue) {
    switch (node.kind) {
        case 'num': return node.value;
        case 'str': return node.value;
        case 'field': return getValue(node.name);
        case 'neg': return -evaluate(node.inner, getValue);
        case 'if': {
            const c = evaluate(node.cond, getValue);
            return c ? evaluate(node.a, getValue) : evaluate(node.b, getValue);
        }
        case 'round': {
            const v = Number(evaluate(node.val, getValue)) || 0;
            const d = Number(evaluate(node.decimals, getValue)) || 0;
            const f = Math.pow(10, d);
            return Math.round(v * f) / f;
        }
        case 'compare': {
            const l = evaluate(node.left, getValue);
            const r = evaluate(node.right, getValue);
            // Loose comparison for = / != to handle string/number mismatches gracefully
            switch (node.op) {
                case '=':  return String(l) === String(r) || l == r; // eslint-disable-line eqeqeq
                case '!=': return !(String(l) === String(r) || l == r); // eslint-disable-line eqeqeq
                case '>=': return Number(l) >= Number(r);
                case '<=': return Number(l) <= Number(r);
                case '>':  return Number(l) > Number(r);
                case '<':  return Number(l) < Number(r);
                default: throw new Error(`Unknown comparison operator: ${node.op}`);
            }
        }
        case 'binop': {
            const l = Number(evaluate(node.left, getValue)) || 0;
            const r = Number(evaluate(node.right, getValue)) || 0;
            switch (node.op) {
                case '+':   return l + r;
                case '-':   return l - r;
                case '*':   return l * r;
                case 'div': return r === 0 ? 0 : l / r;
                default: throw new Error(`Unknown operator: ${node.op}`);
            }
        }
        default: throw new Error(`Unknown node kind: ${node.kind}`);
    }
}

function evaluateExpression(expr, getValue) {
    const tokens = tokenize(expr);
    const ast = parse(tokens);
    return evaluate(ast, getValue);
}

export { evaluateExpression, tokenize, parse };
